import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import { AllianceAppBar } from './alliance/alliance-app-bar';
import { ConnectionBanner, UpdatedBanner } from './alliance/app-banners';
import { AppNavDrawer } from './alliance/app-nav-drawer';
import { BarBadge, BarButton } from './alliance/bar-controls';
import { CURRENT_APP } from './alliance/current-app';
import { SessionExpiredDialog } from './alliance/session-expired-dialog';
import { UserControl } from './alliance/user-control';
import { NavDrawerBody, NavTopBar, useNavData } from './navigation';
import { useCanViewSection } from './navigation/nav-section';

/** Nav drawer width (design: 300px) */
const NAV_DRAWER_WIDTH = 300;

/**
 * Below these widths ARC's nav links fold into the drawer (measured with the
 * real fonts, plus room for a long name in the user control). ARC's links
 * need it even without Admin.
 */
const NAV_BREAKPOINT = { admin: 1240, default: 1140 };

/**
 * The notifications bell for the actions slot. The count is the unread texts
 * and actions the notifications drawer last loaded; clicking toggles that
 * drawer, as before.
 */
function NotificationsButton() {
  // Context
  const {
    dispatch,
    state: { notificationsData, notificationsOpen },
  } = React.useContext(AppContext);

  // Unread Texts
  const unreadTexts = notificationsData.texts.filter((i) => !i.MsgRead);
  // Unread Actions
  const unreadActions = notificationsData.actions.filter((i) => i.Read === '0');
  // Unread Notifications
  const unreadNotifications = unreadActions.length + unreadTexts.length;

  return (
    <BarButton
      square
      open={notificationsOpen}
      aria-label={
        unreadNotifications
          ? `Notifications, ${unreadNotifications} unread`
          : 'Notifications'
      }
      onClick={() => dispatch({ type: 'TOGGLE_NOTIFICATIONS_DRAWER' })}
    >
      {unreadNotifications > 0 ? (
        <NotificationsIcon sx={{ fontSize: 21 }} />
      ) : (
        <NotificationsNoneIcon sx={{ fontSize: 21 }} />
      )}
      <BarBadge
        count={unreadNotifications}
        background={CURRENT_APP.accent}
        color="#ffffff"
        title="Unread notifications"
        position={{ top: -7, right: -7 }}
      />
    </BarButton>
  );
}

/**
 * ARC's App Bar: brand, the top-nav dropdowns (wide screens) or the menu
 * button and nav drawer (narrower, see NAV_BREAKPOINT), the notifications
 * bell, the user control (with Account and RecruitLink) and the App Launcher. Rendered once by the shell
 * (components/dashboard.tsx), in the column above the page.
 *
 * Also mounts what every signed-in page needs once: the connection and
 * "updated" banner sources (shown by <AppBanners /> under the bar) and the
 * session-expired dialog.
 */
export function AppShellBar() {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  const authContext = React.useContext(AuthContext);
  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const navData = useNavData();
  const canViewSection = useCanViewSection();
  const identity = useAllianceIdentity();
  const { signOut, stopImpersonating, signInAgain } = useSessionActions();

  const closeDrawer = React.useCallback(
    () => dispatch({ type: 'DRAWER_CLOSE' }),
    [dispatch],
  );

  React.useEffect(() => {
    // Close the drawer once a link has been followed
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  const showsAdmin = navData.some(
    (section) => section.title === 'Admin' && canViewSection(section),
  );

  return (
    <React.Fragment>
      <AllianceAppBar
        position="relative"
        homeTo="/home"
        loading={state.routeLoadingCount > 0}
        nav={<NavTopBar data={navData} />}
        navBreakpoint={
          showsAdmin ? NAV_BREAKPOINT.admin : NAV_BREAKPOINT.default
        }
        onMenuClick={() => dispatch({ type: 'TOGGLE_DRAWER' })}
        onMenuClose={closeDrawer}
        menuOpen={state.drawerOpen}
        actions={<NotificationsButton />}
        user={
          identity ? (
            <UserControl
              identity={identity}
              onSignOut={signOut}
              onStopImpersonating={stopImpersonating}
              actions={[
                {
                  label: 'Account',
                  icon: <ManageAccountsIcon />,
                  onClick: () => navigate('/account/info'),
                },
              ]}
            />
          ) : undefined
        }
      />

      <AppNavDrawer
        permanent={false}
        width={NAV_DRAWER_WIDTH}
        homeTo="/home"
        open={state.drawerOpen}
        onClose={closeDrawer}
      >
        <NavDrawerBody data={navData} />
      </AppNavDrawer>

      {/* Banner sources: each keeps its banner in the queue while it applies */}
      <ConnectionBanner />
      <UpdatedBanner pathname={location.pathname} />

      <SessionExpiredDialog
        open={authContext.state.tokenError}
        onSignIn={signInAgain}
        onSignOut={signOut}
      />
    </React.Fragment>
  );
}
